import { I18n } from '@calendly/i18n'
import { ContextualBanner } from '@calendly/ui/components/contextual-banner'
import { IconButton } from '@calendly/ui/components/icon-button'
import { AlertWarningTriangleIcon, CloseMiniIcon } from '@calendly/ui/components/icons'
import { Link } from '@calendly/ui/components/link'
import { Loader } from '@calendly/ui/components/loader'
import { SafeColorsContext } from '@calendly/ui/hooks/use-safe-colors'
import { Link as RouterLink, useHistory } from 'react-router-dom'

import {
  selectById as selectCalendarConnectionById,
  useDisconnectCalendarMutation,
  useFetchCalendarAccountsQuery
} from 'features/calendar_connections'

import { CALENDAR_CONNECTIONS_POPUP_ROUTE } from '../constants'
import { Actions, Card, CardButton, Content } from '../integration_card'
import { Error as CalendarAccountError } from './error'
import { headerId, ProviderHeader, ProviderLogo } from './header'
import { useIntegrationProviderError } from './use_integration_provider_error'

const AccountError = ({ id }) => {
  const { accountError, blockedError, queryError } = useIntegrationProviderError({ id })

  if (accountError) {
    return <CalendarAccountError accountId={id} />
  }

  if (queryError || blockedError) {
    return (
      <ContextualBanner variant="danger" icon={AlertWarningTriangleIcon}>
        {queryError || blockedError}
      </ContextualBanner>
    )
  }

  return null
}

const DisconnectAccount = ({ id, kind, allowDisconnect }) => {
  const [disconnect, { isLoading }] = useDisconnectCalendarMutation()

  if (!allowDisconnect) return null
  if (isLoading) return <Loader size="small" />

  return (
    <IconButton
      size="small"
      onClick={() => disconnect(id)}
      aria-describedby={headerId(kind)}
      title={I18n.t('publisher.action.disconnect')}
    >
      <CloseMiniIcon />
    </IconButton>
  )
}

export const CalendarAccount = ({ id }) => {
  const history = useHistory()
  const { data } = useFetchCalendarAccountsQuery()
  const { kind, identifier, pullCalendarIds, allowDisconnect } = selectCalendarConnectionById(data, id)
  const { hasError } = useIntegrationProviderError({ id })

  const pullCalendarPopupPath = CALENDAR_CONNECTIONS_POPUP_ROUTE.getPullAccountPath(id)
  const onCardClickHandler = () => history.push(pullCalendarPopupPath)

  return (
    <Card>
      {!hasError && (
        <CardButton
          onClick={onCardClickHandler}
          aria-label={I18n.t('publisher.availability_settings.section.calendar_settings.check_conflicts.check_with_email_label', {
            count: (pullCalendarIds ?? []).length,
            email: identifier
          })}
        />
      )}
      <ProviderLogo provider={kind} />
      <ProviderHeader provider={kind} subtitle={identifier} id={headerId(kind)} />
      <Content>
        {hasError ? (
          <AccountError id={id} />
        ) : (
          <SafeColorsContext safeColors>
            <Link underline={false} as={RouterLink} to={pullCalendarPopupPath}>
              {I18n.t('publisher.availability_settings.section.calendar_settings.check_conflicts.check_label', {
                count: (pullCalendarIds ?? []).length
              })}
            </Link>
          </SafeColorsContext>
        )}
      </Content>
      <Actions>
        <DisconnectAccount id={id} kind={kind} allowDisconnect={allowDisconnect} />
      </Actions>
    </Card>
  )
}
